import { useEffect, useMemo, useState } from 'react';
import { FiFileText, FiUploadCloud } from 'react-icons/fi';
import { FieldSelectSearch } from '../../../../../_CORE/APP/Templates/_UI/Form/_Fields/FieldSelectSearch';
import { EmailSectionLayout } from '../_UI/EmailSectionLayout';
import { EmailFieldHint, EmailFieldLabel } from '../_UI/EmailFieldGuide';
import { useEmailAdmin } from '../_Hooks/useEmailAdmin';
import { EMAIL_ADMIN_CONSTANTS as C } from '../_MODULES/_API';

export const EmailImportView = () => {
    const api = useEmailAdmin();
    const [file, setFile] = useState(null);
    const [source, setSource] = useState('');
    const [list, setList] = useState(null);
    const [lists, setLists] = useState([]);
    const [message, setMessage] = useState('');
    const [summary, setSummary] = useState(null);
    const [history, setHistory] = useState([]);

    const load = async () => {
        try {
            const [imports, listRows] = await Promise.all([api.get(C.IMPORTS), api.get(C.LISTS)]);
            setHistory(imports || []); setLists(listRows || []);
        } catch { setHistory([]); setLists([]); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const listOptions = useMemo(() => lists.filter((item) => item.active).map((item) => ({ value: item.id, label: `${item.name} · ${item.contacts_total || 0} contactos` })), [lists]);

    const submit = async (event) => {
        event.preventDefault();
        if (!file) { setMessage('Selecciona un archivo.'); return; }
        const body = new FormData();
        body.append('file', file);
        body.append('source', source);
        if (list?.value) body.append('list_id', String(list.value));
        try {
            const result = await api.securePost(C.CONTACT_IMPORT, body);
            setSummary(result); setMessage(''); setFile(null); await load();
            event.currentTarget.reset();
        } catch (err) { setMessage(err.message); }
    };

    return (
        <EmailSectionLayout title="Importar contactos" description="Carga listas desde Excel, CSV, bloc de notas, JSON o Word y asignalas opcionalmente a una lista del sistema.">
            <div className="PBMAIL__import_grid">
                <section className="PBADMIN__panel PBMAIL__panel">
                    <div className="PBADMIN__panel_header"><div><h2>Nueva importación</h2><p>Se detectan emails, se validan y se eliminan duplicados dentro del archivo.</p></div></div>
                    {message && <div className="PBADMIN__alert">{message}</div>}
                    <form className="PBMAIL__import_form" onSubmit={submit}>
                        <div className="PBMAIL__mini_steps">
                            <span><b>1</b> Elegir archivo</span><span><b>2</b> Identificar fuente</span><span><b>3</b> Lista opcional</span><span><b>4</b> Importar</span>
                        </div>
                        <label className="PBMAIL__dropzone">
                            <span className="PBMAIL__dropzone_badge">Necesario</span>
                            <FiUploadCloud />
                            <strong>{file ? file.name : 'Seleccionar archivo'}</strong>
                            <span>Ej: contactos_rrhh.xlsx - XLSX, CSV, TXT, JSON o DOCX - maximo 15 MB</span>
                            <input type="file" accept=".xlsx,.csv,.txt,.json,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        </label>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="recommended">Fuente / referencia</EmailFieldLabel>
                            <input placeholder="Ej: LinkedIn - empresas fintech - agosto 2026" value={source} onChange={(e) => setSource(e.target.value)} />
                            <EmailFieldHint>No afecta el envio. Sirve para recordar de donde salieron esos contactos.</EmailFieldHint>
                        </label>
                        <div className="PBMAIL__select_field">
                            <FieldSelectSearch id="email-import-list" label="Agregar importados a una lista" hint="Opcional: si elegis una lista, los contactos nuevos y actualizados quedan asociados automaticamente. Si no, igual se guardan en Contactos." value={list} onChange={setList} options={listOptions} placeholder="Ej: RRHH Argentina..." />
                        </div>
                        <div className="PBMAIL__import_help">
                            <strong>Que tiene que tener el archivo?</strong>
                            <span><b>Necesario:</b> una direccion de email valida. <b>Opcional:</b> nombre, apellido, empresa, puesto/rol y tags.</span>
                            <small>Columnas reconocidas: email/correo, nombre, apellido, empresa, puesto/rol y tags. Para TXT o DOCX alcanza con que contengan direcciones de email.</small>
                        </div>
                        <button className="PBADMIN__primary" type="submit" disabled={api.isLoading}><FiUploadCloud /> {api.isLoading ? 'Importando...' : 'Importar contactos'}</button>
                    </form>
                </section>

                <section className="PBADMIN__panel PBMAIL__panel">
                    <div className="PBADMIN__panel_header"><div><h2>Resultado</h2><p>Resumen de la última importación realizada.</p></div></div>
                    {!summary && <div className="PBADMIN__empty">Todavía no importaste un archivo en esta sesión.</div>}
                    {summary && (
                        <div className="PBMAIL__import_summary">
                            <div><strong>{summary.detected_count ?? 0}</strong><span>detectados</span></div>
                            <div><strong>{summary.imported_count ?? 0}</strong><span>nuevos</span></div>
                            <div><strong>{summary.updated_count ?? 0}</strong><span>actualizados</span></div>
                            <div><strong>{summary.duplicate_count ?? 0}</strong><span>duplicados</span></div>
                            <div><strong>{summary.invalid_count ?? 0}</strong><span>inválidos</span></div>
                        </div>
                    )}
                    {summary?.list_id ? <div className="PBMAIL__import_list_note">Importación asociada a una lista de contactos.</div> : null}
                    {summary?.preview?.length > 0 && <div className="PBMAIL__preview_list">{summary.preview.slice(0, 8).map((row) => <div key={row.email}><span>{[row.first_name, row.last_name].filter(Boolean).join(' ') || 'Sin nombre'}</span><strong>{row.email}</strong></div>)}</div>}
                </section>
            </div>

            <section className="PBADMIN__panel PBMAIL__panel">
                <div className="PBADMIN__panel_header"><div><h2>Historial de importaciones</h2><p>Qué archivo se procesó y cuántos registros aportó.</p></div></div>
                <div className="PBMAIL__import_history">
                    {history.length === 0 && <div className="PBADMIN__empty">No hay importaciones registradas.</div>}
                    {history.map((item) => (
                        <article key={item.id}><FiFileText /><div><strong>{item.original_name}</strong><span>{item.source || item.format}</span></div><div><b>{item.imported_count}</b> nuevos · <b>{item.updated_count}</b> actualizados · <b>{item.invalid_count}</b> inválidos</div><time>{item.created_at}</time></article>
                    ))}
                </div>
            </section>
        </EmailSectionLayout>
    );
};
